import * as d3 from 'd3';

/**
 * @param svg
 * @param updateSelection
 * @returns {{clear: clear, update}}
 * @constructor
 */
export function NodeLinkChart({ svg, updateSelection }) {
	let initialized = false;
	let zoomEvent = null;
	let constantSize = false;
	const defaultSize = 7;
	let colorScale = null;
	let savedDimensions = { height: 0, width: 0 };

	// radius scale (probably should be configurable min/max)
	const radiusScale = d3.scaleSqrt().range([3, 30]);

	// the force simulation
	const simulation = d3
		.forceSimulation()
		.alphaDecay(0.007)
		.force('charge', d3.forceManyBody().strength(-80))
		.force('x', d3.forceX())
		.force('y', d3.forceY());

	const zoom = d3.zoom().scaleExtent([0.1, 10]).on('start', zoomStart).on('zoom', zoomed).on('end', zoomEnd);

	function ticked() {
		// update node positions
		d3.select(svg)
			.select('.nodes')
			.selectAll('.node')
			.attr('transform', (d) => `translate(${transformX(d.x)}, ${transformY(d.y)})`);

		// update link positions
		d3.select(svg)
			.select('.links')
			.selectAll('line')
			.attr('x1', (d) => transformX(d.source.x))
			.attr('y1', (d) => transformY(d.source.y))
			.attr('x2', (d) => transformX(d.target.x))
			.attr('y2', (d) => transformY(d.target.y));

		// update label positions and hide overlapping labels
		const nodeList = {};
		d3.select(svg)
			.select('.labels')
			.selectAll('.label')
			.attr('x', (d) => transformX(d.x))
			.attr('y', (d) => transformY(d.y))
			.attr('dy', (d) => (constantSize ? -defaultSize - 2 : -radiusScale(d.sizeValue) - 2))
			.classed('visible', true)
			.each(function (d) {
				const bbox = d3.select(this).node().getBBox();
				if (!intersectsAny(bbox, Object.values(nodeList))) {
					nodeList[d.label] = bbox;
				}
			})
			.classed('visible', (d) => !!nodeList[d.label]);
	}

	function transformX(x) {
		if (zoomEvent == null) return x;
		return zoomEvent.transform.applyX(x);
	}

	function transformY(y) {
		if (zoomEvent == null) return y;
		return zoomEvent.transform.applyY(y);
	}

	function inverseTransformX(x) {
		if (zoomEvent == null) return x;
		return zoomEvent.transform.invertX(x);
	}

	function inverseTransformY(y) {
		if (zoomEvent == null) return y;
		return zoomEvent.transform.invertY(y);
	}

	// ******* ZOOM HANDLERS **************
	function zoomStart() {
		d3.select(svg).selectAll('.label').classed('visible', false);
	}

	function zoomed(event) {
		zoomEvent = event;
		simulation.restart().tick();
	}

	function zoomEnd() {
		ticked();
	}

	// ********* DRAG HANDLERS ***********
	function dragStart(event, d) {
		event.sourceEvent.stopPropagation();
		if (!event.active) simulation.alphaTarget(0.3).restart();
		d.fx = d.x;
		d.fy = d.y;
		d.cursor = d3.select(this).style('cursor');
		d3.select(this).style('cursor', 'grab');
	}

	function dragged(event, d) {
		d.fx = inverseTransformX(event.sourceEvent.offsetX);
		d.fy = inverseTransformY(event.sourceEvent.offsetY);
	}

	function dragEnd(event) {
		if (!event.active) simulation.alphaTarget(0);
	}

	/**
	 * Init method
	 *
	 * Manage the initialization of the chart, should only be called once
	 * things which need the data, dimensions, options, or don't need to be in scope
	 */
	function init(data, { height, width }) {
		d3.select(svg).call(zoom);
		d3.select(svg).append('g').classed('links', true);
		d3.select(svg).append('g').classed('nodes', true);
		d3.select(svg).append('g').classed('labels', true);

		zoomEvent = null;
		simulation.on('tick', ticked);

		savedDimensions = { height, width };
		d3.select(svg).call(zoom.transform, d3.zoomIdentity.translate(width / 2, height / 2));
	}

	/**
	 * Update method
	 *
	 * @param nodes
	 * @param links
	 * @param height
	 * @param width
	 * @param localColorScale
	 * @param selection
	 */
	function update({ nodes, links }, { height, width }, { colorScale: localColorScale }, selection) {
		colorScale = localColorScale;
		constantSize = false;
		const extent = d3.extent(nodes, (d) => Number(d.count));
		if (extent[0] === extent[1]) constantSize = true;
		radiusScale.domain(extent);

		// recenter only when the size changes
		if (height !== savedDimensions.height || width !== savedDimensions.width)
			d3.select(svg).call(
				zoom.transform,
				d3.zoomIdentity.translate(width / 2, height / 2).scale(zoomEvent.transform.k)
			);

		d3.select(svg)
			.select('.nodes')
			.selectAll('.node')
			.data(nodes, (d) => d.label)
			.join(
				(enter) =>
					enter
						.append('g')
						.classed('node', true)
						.on('click', (_, d) => {
							const newValue = {
								...d,
								fx: null,
								fy: null,
							};
							if (selection.includes(newValue)) {
								updateSelection(null);
							} else {
								updateSelection(newValue);
							}
						})
						.on('mouseover', (event, d) => {
							mouseOverNode(event, d);
						})
						.on('mouseout', (event, d) => {
							mouseOutNode(event, d);
						})
						.call(d3.drag().on('start', dragStart).on('drag', dragged).on('end', dragEnd))
						// add the node markers
						.call((enter) => {
							enter
								.append('circle')
								.classed('node-circle', true)
								.attr('cy-test', 'nodeLink-data-node')
								.attr('r', (d) => (constantSize ? defaultSize : radiusScale(d.sizeValue)))
								.attr('fill', (d) => colorScale(d.colorValue))
								.attr('stroke', (d) => (selection.includes(d) ? 'black' : 'white'))
								.attr('stroke-width', (d) => (selection.includes(d) ? 3 : 1));

							return enter;
						}),
				(update) => {
					update
						.selectAll('.node-circle')
						.attr('r', (d) => {
							return constantSize ? defaultSize : radiusScale(d.sizeValue);
						})
						.attr('fill', (d) => colorScale(d.colorValue))
						.attr('stroke', (d) => (selection.includes(d) ? 'black' : 'white'))
						.attr('stroke-width', (d) => (selection.includes(d) ? 3 : 1));

					return update;
				},
				(exit) => {
					return exit.remove();
				}
			);

		d3.select(svg)
			.select('.labels')
			.selectAll('.label')
			.data(nodes, (d) => d.label)
			.join(
				(enter) =>
					enter
						.append('text')
						.classed('label', true)
						.text((d) => d.label)
						.attr('fill', 'black')
						.attr('font-size', 10)
						.attr('text-anchor', 'middle'),
				(update) => update,
				(exit) => exit.remove()
			);

		// add links
		d3.select(svg)
			.select('.links')
			.attr('stroke', 'grey')
			.attr('opacity', 0.5)
			.selectAll('line')
			.data(links, (d) => d.key)
			.join(
				(enter) => enter.append('line'),
				(update) => update,
				(exit) => exit.remove()
			);

		// update the forces, data and restart the simulation
		simulation.force(
			'links',
			d3.forceLink(links).id((d) => d.id)
		);

		simulation.force(
			'collide',
			d3.forceCollide().radius((d) => (constantSize ? defaultSize : radiusScale(d.sizeValue)))
		);

		simulation.nodes(nodes);
		simulation.alpha(1).restart();
	}

	// *******************************************************************************************************************

	/**
	 * Called the the user mouses over a node in the graph
	 * @param event
	 * @param d
	 */
	function mouseOverNode(event, d) {
		// on mouse over make OTHER nodes less visible
		d3.select(svg)
			.selectAll('.node')
			.filter(function (f) {
				return d !== f;
			})
			.call(async function (s) {
				try {
					const _ = await s.select('circle').transition().duration(250).attr('fill', 'lightgrey').end();
					const labels = {};

					// for each neighbor make it more visible
					getNeighboringNodes(d)
						.raise()
						.call((s) => s.select('circle').attr('fill', (d) => colorScale(d.colorValue)))
						.each((d) => (labels[d.label] = ''));

					d3.select(svg)
						.selectAll('.label')
						.classed('visible', (d) => labels[d.label] !== null)
						.classed('nonneighbor', (d) => labels[d.label] == null);

					const links = getConnectedLinks(d);

					// update neighboring links to be more visible
					links.raise().attr('opacity', 1).attr('stroke-width', 2).attr('stroke', colorScale(d.colorValue));

					ticked();
					simulation.stop();
				} catch (e) {}
			});
	}

	/**
	 * Called when the user mouse outs a node
	 * @param event
	 * @param d
	 */
	function mouseOutNode(event, d) {
		// return everything to original state
		d3.select(svg)
			.selectAll('.node')
			.call((s) => {
				s.select('circle')
					.transition()
					.duration(100)
					.attr('fill', (d) => colorScale(d.colorValue));
			});

		d3.select(svg).selectAll('.label').classed('nonneighbor', false);
		const links = getConnectedLinks(d);
		links.transition().duration(100).attr('opacity', 0.5).attr('stroke-width', 1).attr('stroke', 'grey');
		ticked();
		simulation.restart();
	}

	/**
	 * Returns a list of nodes which are connected to the given node
	 * @param d
	 * @returns {*}
	 */
	const getNeighboringNodes = (d) => {
		const links = getConnectedLinks(d);
		const nodeSet = new Set();

		nodeSet.add(d);

		links.each((link) => {
			nodeSet.add(link.source);
			nodeSet.add(link.target);
		});

		return d3
			.select(svg)
			.select('.nodes')
			.selectAll('g')
			.filter(function (node) {
				return nodeSet.has(node);
			});
	};

	/**
	 * Returns a list of links which are connected to the given node
	 *
	 * @param d
	 * @returns {*}
	 */
	const getConnectedLinks = (d) => {
		return d3
			.select(svg)
			.select('.links')
			.selectAll('line')
			.filter(function (l) {
				return l.source === d || l.target === d;
			});
	};

	/**
	 * Return function, calls init the first time through and update all others
	 * @param data
	 * @param dimensions
	 * @param options
	 * @param selection
	 */
	function externalUpdate({ data, dimensions, options, selection }) {
		if (!initialized) {
			init(data, dimensions);
			initialized = true;
		}
		update(data, dimensions, options, selection);
	}

	/**
	 * Clears the state when necessary
	 */
	function clear() {
		d3.select(svg).selectAll('g').remove();
		simulation.stop();
	}

	return {
		update: externalUpdate,
		clear: clear,
	};
}

/**
 * Returns true if the given rect intersects with any in the given list of rects
 * @param test
 * @param list
 * @returns {boolean|*}
 */
function intersectsAny(test, list) {
	if (list.length === 0) return false;

	return list.some((l) => {
		return intersectRect(test, l);
	});
}

/**
 * Tests if the two given rects intersect
 * @param r1
 * @param r2
 */
function intersectRect(r1, r2) {
	return !(r2.x > r1.x + r1.width || r2.x + r2.width < r1.x || r2.y > r1.y + r1.height || r2.y + r2.height < r1.y);
}
